import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { FileDropField } from "@rentablo/components";
import DownloadIcon from "@icons/myicons/download.svg?react";
import {
  CONTENT_TYPE_EXTENSIONS,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
} from "../constants";
import { AttachedFile } from "./AttachedFile";

// && outranks FileDropField's own container class. Plain overrides would
// depend on insertion order, which is not ours to control across packages.
const DropField = styled(FileDropField, {
  shouldForwardProp: (prop) => prop !== "isFull",
})<{ isFull: boolean }>(({ theme, isFull }) => ({
  "&&": {
    height: "auto",
    padding: theme.spacing(2),
    border: "none",
    backgroundColor: isFull
      ? theme.palette.background.neutral
      : theme.palette.background.info,
    cursor: isFull ? "not-allowed" : "pointer",
    opacity: isFull ? 0.6 : 1,
  },
}));

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  onReject: () => void;
};

export const AttachmentsField = ({ files, onChange, onReject }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFull = files.length >= MAX_ATTACHMENTS;

  const hint = isFull
    ? t("customerSupport.attachments.full", { max: MAX_ATTACHMENTS })
    : t("customerSupport.attachments.hint");

  const handleDrop = (accepted: File[]) => {
    if (accepted.length)
      onChange([...files, ...accepted].slice(0, MAX_ATTACHMENTS));
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} flexShrink={0}>
      {files.map((file, index) => (
        <AttachedFile
          key={`${file.name}-${index}`}
          file={file}
          onRemove={() => onChange(files.filter((_, i) => i !== index))}
        />
      ))}
      <DropField
        isFull={isFull}
        testId="support-attachments-drop"
        onDrop={handleDrop}
        onDropRejected={onReject}
        accept={CONTENT_TYPE_EXTENSIONS}
        // showOpenFilePicker turns every accept entry into its own file-type
        // dropdown option and preselects the first, greying out the rest. The
        // input element takes all the types at once.
        useFsAccessApi={false}
        maxSize={MAX_ATTACHMENT_BYTES}
        maxFiles={MAX_ATTACHMENTS - files.length}
        disabled={isFull}
        text={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1}
            textAlign="center"
          >
            <Box
              display="flex"
              flexShrink={0}
              color={theme.palette.text.secondary}
            >
              <DownloadIcon width="20px" height="20px" />
            </Box>
            <Typography variant="body2">
              {t("customerSupport.attachments.label")}
            </Typography>
            <Typography variant="body3" color="textSecondary">
              {hint}
            </Typography>
          </Box>
        }
      />
      <Typography variant="caption" color="textSecondary">
        {t("customerSupport.attachments.limits", { max: MAX_ATTACHMENTS })}
      </Typography>
    </Box>
  );
};
